/**
 * Fantasy Console 0 - Effect Selector Component
 *
 * Row of 8 buttons for selecting the active effect type.
 * Each button shows an SVG icon matching the piano roll effect indicators.
 */

import { EFFECT_NAMES } from '../../utils/soundUtils'
import styles from './EffectSelector.module.css'

/**
 * SVG icons for each effect type, matching the drawEffectIndicator symbols.
 * Rendered in a 16x12 viewbox.
 */
function EffectIcon({ effect }: { effect: number }) {
  const props = {
    width: 16,
    height: 12,
    viewBox: '0 0 16 12',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }

  switch (effect) {
    case 0: // None
      return (
        <svg {...props}>
          <line x1='4' y1='2' x2='12' y2='10' />
          <line x1='4' y1='10' x2='12' y2='2' />
        </svg>
      )
    case 1: // Slide: right arrow →
      return (
        <svg {...props}>
          <line x1='2' y1='6' x2='13' y2='6' />
          <polyline points='10,3 13,6 10,9' />
        </svg>
      )
    case 2: // Vibrato: wavy line ~
      return (
        <svg {...props}>
          <path d='M1 6 Q4 1 8 6 Q12 11 15 6' />
        </svg>
      )
    case 3: // Drop: down arrow ↓
      return (
        <svg {...props}>
          <line x1='8' y1='1' x2='8' y2='10' />
          <polyline points='5,7 8,10 11,7' />
        </svg>
      )
    case 4: // Fade In: left wedge <
      return (
        <svg {...props}>
          <polyline points='12,1 4,6 12,11' />
        </svg>
      )
    case 5: // Fade Out: right wedge >
      return (
        <svg {...props}>
          <polyline points='4,1 12,6 4,11' />
        </svg>
      )
    case 6: // Arp Fast: double caret ^^
      return (
        <svg {...props}>
          <polyline points='1,8 4,3 7,8' />
          <polyline points='9,8 12,3 15,8' />
        </svg>
      )
    case 7: // Arp Slow: single caret ^
      return (
        <svg {...props}>
          <polyline points='3,9 8,2 13,9' />
        </svg>
      )
    default:
      return null
  }
}

interface EffectSelectorProps {
  selected: number
  onSelect: (effect: number) => void
}

export function EffectSelector({ selected, onSelect }: EffectSelectorProps) {
  return (
    <div className={styles.container}>
      <span className={styles.label}>Effect</span>
      <div className={styles.buttons}>
        {EFFECT_NAMES.map((name, i) => (
          <button
            key={i}
            className={`${styles.button} ${i === selected ? styles.selected : ''}`}
            onClick={() => onSelect(i)}
            title={name}
          >
            <EffectIcon effect={i} />
            {/* <span>{name}</span> */}
          </button>
        ))}
      </div>
    </div>
  )
}
